import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import type { PaginatedResponse } from '@/shared/types/api'

export interface GeoZone {
  uuid: string
  name: string
  code: string
  zone_type: string
  metadata: Record<string, unknown>
  parent_zone_name: string | null
  /** The parent's uuid, so an edit form can pre-select it (WPS-222). */
  parent_zone_uuid: string | null
  postal_code_count: number
  child_count: number
  psa_count: number
}

export interface ProductAvailabilityRecord {
  uuid: string
  zone_uuid: string
  zone_name: string
  zone_code: string
  effective_start: string
  effective_end: string | null
}

// ── Geo Zones ──────────────────────────────────────────────

export function useGeoZones(params?: Record<string, string | number>) {
  const enabled = useFeatureFlag(FLAGS.GEOGRAPHIC_AVAILABILITY)
  return useQuery<PaginatedResponse<GeoZone>>({
    queryKey: ['geo-zones', params],
    queryFn: () => api.get('/geo/zones/', { params }).then((r) => r.data),
    enabled,
  })
}

export function useGeoZonesAll(params?: Record<string, string | number>) {
  const enabled = useFeatureFlag(FLAGS.GEOGRAPHIC_AVAILABILITY)
  return useQuery<PaginatedResponse<GeoZone>>({
    queryKey: ['geo-zones-all', params],
    queryFn: () =>
      api
        .get('/geo/zones/', { params: { page_size: 500, ...params } })
        .then((r) => r.data),
    enabled,
  })
}

export function useCreateGeoZone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      name: string
      zone_type: string
      code?: string
      parent_zone_uuid?: string | null
      metadata?: Record<string, unknown>
    }) => api.post('/geo/zones/', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['geo-zones'] })
      qc.invalidateQueries({ queryKey: ['geo-zones-all'] })
    },
  })
}

export function useUpdateGeoZone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: Partial<GeoZone> }) =>
      api.put(`/geo/zones/${uuid}/`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['geo-zones'] })
      qc.invalidateQueries({ queryKey: ['geo-zones-all'] })
    },
  })
}

export function useDeleteGeoZone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => api.delete(`/geo/zones/${uuid}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['geo-zones'] })
      qc.invalidateQueries({ queryKey: ['geo-zones-all'] })
    },
  })
}

// ── Product Availability ───────────────────────────────────

export function useProductAvailability(productUuid: string | null) {
  const geoEnabled = useFeatureFlag(FLAGS.GEOGRAPHIC_AVAILABILITY)
  return useQuery<ProductAvailabilityRecord[]>({
    queryKey: ['product-availability', productUuid],
    queryFn: () =>
      api
        .get<PaginatedResponse<ProductAvailabilityRecord>>(
          `/products/items/${productUuid}/availability/`,
          { params: { page_size: 500 } },
        )
        .then((r) => r.data.results),
    enabled: geoEnabled && !!productUuid,
  })
}

export function useAddProductAvailability() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      productUuid,
      data,
    }: {
      productUuid: string
      data: { zone_uuid: string; effective_start: string; effective_end?: string | null }
    }) =>
      api.post(`/products/items/${productUuid}/availability/`, data).then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['product-availability', vars.productUuid] })
    },
  })
}

export function useRemoveProductAvailability() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      productUuid,
      availUuid,
    }: {
      productUuid: string
      availUuid: string
    }) => api.delete(`/products/items/${productUuid}/availability/${availUuid}/`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['product-availability', vars.productUuid] })
    },
  })
}

export function useUpdateProductAvailability() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      productUuid,
      availUuid,
      data,
    }: {
      productUuid: string
      availUuid: string
      data: { zone_uuid: string; effective_start: string; effective_end?: string | null }
    }) =>
      api
        .put(`/products/items/${productUuid}/availability/${availUuid}/`, data)
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['product-availability', vars.productUuid] })
    },
  })
}
